/**
 * BrandLogo — the single source for the XCRMIQ product logo across the app.
 *
 * Renders the horizontal lockup ("full") or the running-man mark ("mark") from
 * the assets in `public/brand`. Styling is self-contained (inline) so the same
 * component drops cleanly onto every surface — the Tailwind landing/auth pages
 * AND the plain-CSS vendor/admin panels alike.
 *
 * The source artwork sits on a WHITE field (no transparency), so on white /
 * near-white surfaces it renders bare. On dark or coloured surfaces pass
 * `onDark` to wrap it in a white chip — otherwise the dark-grey "Intelligent
 * CRM" tagline (and, for the mark, its white field) would be illegible.
 */
import type { CSSProperties } from 'react'

const SRC = {
  full: '/brand/xcrmiq-logo.png',
  mark: '/brand/xcrmiq-mark.png',
} as const

// Intrinsic aspect ratio (width / height) of each source asset.
const RATIO = { full: 760 / 287, mark: 1 } as const

export type BrandLogoProps = {
  /** Full horizontal lockup (default) or the square mark only. */
  variant?: keyof typeof SRC
  /** Rendered height in px. Width follows the asset's aspect ratio. */
  height?: number
  /** Wrap in a white rounded chip — use on dark/coloured backgrounds. */
  onDark?: boolean
  className?: string
  style?: CSSProperties
}

export function BrandLogo({ variant = 'full', height = 36, onDark = false, className, style }: BrandLogoProps) {
  const img = (
    <img
      src={SRC[variant]}
      alt="XCRMIQ — Intelligent CRM"
      width={Math.round(height * RATIO[variant])}
      height={height}
      style={{ height, width: 'auto', display: 'block' }}
      draggable={false}
    />
  )

  if (!onDark) {
    return (
      <span className={className} style={{ display: 'inline-flex', alignItems: 'center', ...style }}>
        {img}
      </span>
    )
  }

  // White chip keeps the full-colour lockup legible on dark surfaces.
  const padX = Math.round(height * 0.3)
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        background: '#fff',
        borderRadius: Math.round(height * 0.34),
        padding: `${Math.round(padX * 0.66)}px ${padX}px`,
        boxShadow: '0 6px 18px -8px rgba(0, 0, 0, 0.45)',
        ...style,
      }}
    >
      {img}
    </span>
  )
}
